'use client';

import { useUIStore, notify } from './index';
import { toast as sonnerToast } from 'sonner';

/**
 * useToast - Toast notification hook integrated with Zustand UI store
 *
 * This hook provides a wrapper around the UI store's notification system
 * with backward compatibility for the old toast API.
 *
 * Prefer using the `notify` utility directly from the store for new code.
 */
export function useToast() {
  const notifications = useUIStore((state) => state.notifications);
  const addNotification = useUIStore((state) => state.addNotification);
  const removeNotification = useUIStore((state) => state.removeNotification);

  const toast = ({ title, description, variant = 'default', ...props }) => {
    // Use Zustand store notification system
    const type =
      variant === 'destructive'
        ? 'error'
        : variant === 'success'
        ? 'success'
        : 'info';

    // Use sonner toast for immediate feedback
    if (sonnerToast) {
      if (variant === 'destructive') {
        return sonnerToast.error(title, {
          description,
          ...props,
        });
      }

      return sonnerToast(title, {
        description,
        ...props,
      });
    }

    // Add to Zustand store as fallback
    const notification = {
      type,
      message: title,
      description,
      ...props,
    };

    addNotification(notification);

    return {
      id: notification.id,
      dismiss: () => removeNotification(notification.id),
      update: (updateProps) => {
        removeNotification(notification.id);
        addNotification({ ...notification, ...updateProps });
      },
    };
  };

  return {
    toast,
    toasts: notifications,
    dismissToast: removeNotification,
    dismissAllToasts: () => {
      notifications.forEach((n) => removeNotification(n.id));
    },
    // Provide direct access to store notification helpers
    notify,
  };
}
